import { Bounds, Vector } from '@ardoq/graph';
import { renderReference, renderReferenceLabel } from './reference';
import { renderNode } from './node';
import { clamp } from 'lodash';
import { currentTimestamp } from '@ardoq/date-time';
import { AQBLOCKS_TRANSITION_TWEEN } from '../consts';

import { BlocksGraph, BlocksNode } from '../types';

import {
  rectEnclose,
  rectInterp,
  rectIntersects,
  vectorsInterp,
} from 'tabview/visualizations/misc/geometry';

import { createRenderContext } from './rendercontext';
import { RenderParameters } from './types';
import { isNodeOpen } from '../util';

const tween = AQBLOCKS_TRANSITION_TWEEN;

export const renderSceneGraph = (
  graph: BlocksGraph,
  params: RenderParameters
) => {
  const window = params.projection.window;
  const renderContext = createRenderContext(params);
  const now = currentTimestamp();

  let done = true;

  params.ctx.save();

  const doit = (node: BlocksNode) => {
    const { animation } = node;
    let { cell } = node;
    let globalAlpha = 1;

    if (!cell) {
      return;
    }

    if (animation) {
      const p = clamp((now - animation.begin) / animation.duration, 0, 1);

      if (p < 1) {
        if (animation.from) {
          cell = rectInterp(tween(p), animation.from as Bounds, cell, null);
        } else {
          globalAlpha = p;
        }

        done = false;
      } else {
        node.animation = undefined;
      }
    }

    if (cell && rectIntersects(window, cell)) {
      if (node.parent) {
        done = renderNode(node, cell, globalAlpha, renderContext) && done;
      }

      if (node.children && isNodeOpen(node)) {
        for (const child of node.children) {
          doit(child);
        }
      }
    }
  };

  doit(graph.root);

  for (const reference of graph.edges) {
    if (!reference.route || !reference.bounds) {
      continue;
    }

    const { animation } = reference;
    let { route, bounds } = reference;
    let globalAlpha = 1;

    if (animation) {
      const p = clamp((now - animation.begin) / animation.duration, 0, 1);

      if (p < 1) {
        if (animation.from) {
          route = vectorsInterp(
            tween(p),
            animation.from as Vector[],
            route,
            null
          );
        } else {
          route = vectorsInterp(tween(p), [], route, null);
          globalAlpha = p;
        }

        bounds = rectEnclose(...route);
        done = false;
      } else {
        reference.animation = undefined;
      }
    }

    if (bounds && rectIntersects(window, bounds)) {
      done =
        renderReference(reference, bounds, route, globalAlpha, renderContext) &&
        done;
      renderReferenceLabel(
        reference,
        bounds,
        route,
        globalAlpha,
        renderContext
      );
    }
  }

  params.ctx.restore();

  return done;
};
